/**
 * Entity - Base class for all game entities (players, robots, collectibles)
 * Provides common functionality for position, velocity, lifecycle, and updates
 */

import type { Vector2 } from '../../types/game';
import { getPhysicsEngine } from '../../engine/PhysicsEngine';
import { getCollisionSystem } from '../../engine/CollisionSystem';
import { debugSettings } from '../../utils/DebugSettings';
import { playFootstepSound, stopFootstepSound } from '../audio/SoundEffects';
import { audioManager } from '../../engine/AudioManager';

export abstract class Entity {
  // Core properties
  public id: string;
  public type: string;
  public active: boolean = true;
  public destroyed: boolean = false;

  // Transform
  public position: Vector2;
  public velocity: Vector2;
  public size: { width: number; height: number };
  public rotation: number = 0;

  // State (public for direct access from scene/UI)
  public health: number = 100;
  public maxHealth: number = 100;
  protected wasGrounded: boolean = false; // Track previous grounded state for landing detection
  protected previousVelocityY: number = 0; // Track previous frame's Y velocity for landing detection

  // Footstep audio
  protected footstepSoundId: number | null = null; // Track footstep sound for stopping
  protected isPlayingFootsteps: boolean = false; // Track if footsteps are playing
  protected footstepCheckCounter: number = 0; // Counter to reduce footstep check frequency

  constructor(
    id: string,
    type: string,
    position: Vector2,
    size: { width: number; height: number }
  ) {
    this.id = id;
    this.type = type;
    this.position = { ...position };
    this.velocity = { x: 0, y: 0 };
    this.size = { ...size };
  }

  /**
   * Initialize the entity (called after construction)
   * Override this in subclasses to add custom initialization
   */
  public initialize(): void {
    // Register with physics and collision systems by default
    this.registerPhysics();
    this.registerCollision();
  }

  /**
   * Register entity with physics engine
   * Override to customize physics properties
   */
  protected registerPhysics(): void {
    const physicsEngine = getPhysicsEngine();
    // Pass 'this' directly - physics engine stores reference to entity
    physicsEngine.addEntity(this as any, {
      mass: 1.0,
      gravity: 1.0,
      friction: 0.8,
      restitution: 0.0,
      isKinematic: false,
      isGrounded: false,
      canJump: false,
    });
  }

  /**
   * Register entity with collision system
   * Override to customize collision properties
   */
  protected registerCollision(): void {
    const collisionSystem = getCollisionSystem();
    // Pass 'this' directly - collision system stores reference to entity
    collisionSystem.addCollider(this as any, {
      type: 'box',
      isTrigger: false,
      layer: 'static',
    });
  }

  /**
   * Update entity logic (called every frame)
   * Override this in subclasses to add custom behavior
   */
  public abstract update(deltaTime: number): void;

  /**
   * Render entity (called every frame after update)
   * Override this in subclasses to add custom rendering
   */
  public abstract render(): void;

  /**
   * Handle taking damage
   */
  public takeDamage(amount: number): void {
    if (this.destroyed) return;

    this.health = Math.max(0, this.health - amount);

    if (this.health <= 0) {
      this.onDeath();
    }
  }

  /**
   * Handle healing
   */
  public heal(amount: number): void {
    if (this.destroyed) return;
    this.health = Math.min(this.maxHealth, this.health + amount);
  }

  /**
   * Get current health
   */
  public getHealth(): number {
    return this.health;
  }

  /**
   * Get max health
   */
  public getMaxHealth(): number {
    return this.maxHealth;
  }

  /**
   * Check if entity is alive
   */
  public isAlive(): boolean {
    return this.health > 0 && !this.destroyed;
  }

  /**
   * Called when entity dies
   * Override to add custom death behavior
   */
  protected onDeath(): void {
    this.destroy();
  }

  /**
   * Destroy the entity
   */
  public destroy(): void {
    if (this.destroyed) return;

    this.destroyed = true;
    this.active = false;

    // Stop footsteps if playing
    if (this.isPlayingFootsteps) {
      stopFootstepSound(this.footstepSoundId);
      this.footstepSoundId = null;
      this.isPlayingFootsteps = false;
    }

    // Unregister from systems
    const physicsEngine = getPhysicsEngine();
    const collisionSystem = getCollisionSystem();

    physicsEngine.removeEntity(this.id);
    collisionSystem.removeCollider(this.id);

    // Call cleanup hook
    this.onDestroy();
  }

  /**
   * Called when entity is destroyed
   * Override to add custom cleanup
   */
  protected onDestroy(): void {
    // Override in subclasses
  }

  /**
   * Called when entity lands on the ground
   * Override to add custom landing behavior (sounds, particles, etc.)
   * @param landingVelocity - The vertical velocity when landing (negative = falling)
   */
  protected onLanding(landingVelocity: number): void {
    // Override in subclasses
  }

  /**
   * Set entity position
   */
  public setPosition(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
  }

  /**
   * Set entity velocity
   */
  public setVelocity(x: number, y: number): void {
    this.velocity.x = x;
    this.velocity.y = y;
  }

  /**
   * Apply force to entity
   */
  public applyForce(force: Vector2, deltaTime: number): void {
    const physicsEngine = getPhysicsEngine();
    physicsEngine.applyForce(this.id, force, deltaTime);
  }

  /**
   * Apply impulse to entity (instant force)
   */
  public applyImpulse(impulse: Vector2): void {
    const physicsEngine = getPhysicsEngine();
    physicsEngine.applyImpulse(this.id, impulse);
  }

  /**
   * Check if entity is grounded (on a platform)
   */
  public isGrounded(): boolean {
    const physicsEngine = getPhysicsEngine();
    return physicsEngine.isGrounded(this.id);
  }

  /**
   * Check for landing and trigger landing callback
   * Should be called AFTER collision update to properly detect grounded state
   */
  public checkLanding(): void {
    const isGrounded = this.isGrounded();

    // Detect landing: transition from airborne to grounded
    if (!this.wasGrounded && isGrounded) {
      // Entity just landed - use previous velocity since current velocity was zeroed by collision
      const landingVelocity = this.previousVelocityY;
      console.log(`🟢 [${this.type}] LANDING DETECTED! Previous velocity: ${landingVelocity}, Current velocity: ${this.velocity.y}, wasGrounded: ${this.wasGrounded}, isGrounded: ${isGrounded}`);
      this.onLanding(landingVelocity);
    }

    // Update previous grounded state
    this.wasGrounded = isGrounded;
  }

  /**
   * Update footstep audio based on movement and grounded state
   * Should be called every frame in update()
   */
  public setFootstepVolume(volume: number): void {
    if (!this.isPlayingFootsteps || this.footstepSoundId === null) return;
    audioManager.setSFXInstanceVolume('footstep', this.footstepSoundId, volume);
  }

  public updateFootsteps(): void {
    // Check every 10 frames to prevent lag
    this.footstepCheckCounter++;
    if (this.footstepCheckCounter < 10) {
      return;
    }
    this.footstepCheckCounter = 0;

    const isGrounded = this.isGrounded();
    const isMoving = Math.abs(this.velocity.x) > 10;

    const shouldPlayFootsteps = isGrounded && isMoving;

    if (shouldPlayFootsteps && !this.isPlayingFootsteps) {
      this.footstepSoundId = playFootstepSound();
      this.isPlayingFootsteps = true;
    } else if (!shouldPlayFootsteps && this.isPlayingFootsteps) {
      stopFootstepSound(this.footstepSoundId);
      this.footstepSoundId = null;
      this.isPlayingFootsteps = false;
    }
  }

  /**
   * Get distance to another entity
   */
  public distanceTo(other: Entity): number {
    const dx = this.position.x - other.position.x;
    const dy = this.position.y - other.position.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Get direction to another entity (normalized)
   */
  public directionTo(other: Entity): Vector2 {
    const dx = other.position.x - this.position.x;
    const dy = other.position.y - this.position.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) {
      return { x: 0, y: 0 };
    }

    return {
      x: dx / length,
      y: dy / length,
    };
  }
}
